import store from '../store.js'

export const GA4_BRAND = 'Bat Norton'
export const GA4_SITE_ID = 2

function toNumber(value) {
    if (value === null || value === undefined || value === '') {
        return undefined
    }
    const n = Number(value)
    return Number.isFinite(n) ? n : undefined
}

function genderLabel(source) {
    const gender = source?.gender ?? (source?.gender_id == 1 ? 'male' : source?.gender_id == 2 ? 'female' : store.state.settings?.gender)
    if (gender === 'male' || gender === 1 || gender === '1') {
        return 'Men'
    }
    if (gender === 'female' || gender === 2 || gender === '2') {
        return 'Women'
    }
    return undefined
}

function sizeCode(source) {
    return source?.offer?.size?.code
        || source?.size?.code
        || source?.size_code
        || (typeof source?.size === 'string' ? source.size : '')
        || ''
}

function colorName(source) {
    if (typeof source?.color === 'string') {
        return source.color
    }
    return source?.color?.name || source?.color_name || ''
}

function categoryName(source) {
    return source?.category_name || source?.menu_data?.category_name || ''
}

function itemId(source) {
    const vendor = source?.offer?.vendorCode || source?.vendorCode || source?.vendor_code
    if (vendor) {
        return String(vendor)
    }
    const id = source?.card_id || source?.id
    const size = sizeCode(source)
    return size ? `${id}-${size}` : String(id ?? '')
}

function itemPrice(source) {
    return toNumber(source?.offer?.price_num ?? source?.price_num ?? source?.price)
}

function itemDiscount(source) {
    const oldPrice = toNumber(source?.offer?.price_old_num ?? source?.price_old_num)
    const price = itemPrice(source)
    if (oldPrice !== undefined && price !== undefined && oldPrice > price) {
        return oldPrice - price
    }
    const explicit = toNumber(source?.discount)
    return explicit
}

function omitEmpty(object) {
    return Object.fromEntries(
        Object.entries(object).filter(([, value]) => value !== undefined && value !== null && value !== '')
    )
}

export function isEnabled() {
    return Number(store.state.settings?.site_id) === GA4_SITE_ID
}

export function currencyCode(source) {
    return source?.currency_code || store.state.settings?.currency || undefined
}

export function toItem(source, extras = {}) {
    if (!source) {
        return null
    }
    const color = colorName(source)
    const size = sizeCode(source)
    const variant = color && size
        ? `${color} / Size ${size}`
        : (color || (size ? `Size ${size}` : undefined))
    const discount = itemDiscount(source)
    const quantity = toNumber(source?.count ?? source?.quantity ?? extras.quantity) ?? 1

    return omitEmpty({
        item_id: itemId(source),
        item_name: source.name,
        item_brand: GA4_BRAND,
        item_category: categoryName(source),
        item_category2: genderLabel(source),
        item_category3: source.subcategory_name,
        item_variant: variant,
        price: itemPrice(source),
        quantity,
        discount,
        index: extras.index,
        item_list_id: extras.item_list_id ?? source.item_list_id,
        item_list_name: extras.item_list_name ?? source.item_list_name,
    })
}

export function itemsValue(items) {
    return items.reduce((sum, item) => {
        const price = toNumber(item?.price) ?? 0
        const quantity = toNumber(item?.quantity) ?? 1
        return sum + price * quantity
    }, 0)
}

export function pushEvent(event, ecommerce = {}) {
    if (!isEnabled() || typeof window === 'undefined') {
        return
    }
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
        event,
        ecommerce: omitEmpty(ecommerce),
    })
}

export function viewItemList(cards, listId, listName, indexOffset = 0) {
    const items = (cards || [])
        .map((card, index) => toItem(card, {
            index: indexOffset + index + 1,
            item_list_id: listId,
            item_list_name: listName,
        }))
        .filter(Boolean)
    if (!items.length) {
        return
    }
    pushEvent('view_item_list', {
        item_list_id: listId,
        item_list_name: listName,
        items,
    })
}

export function selectItem(card, listId, listName, index) {
    const item = toItem(card, {
        index,
        item_list_id: listId,
        item_list_name: listName,
        quantity: undefined,
    })
    if (!item) {
        return
    }
    delete item.quantity
    pushEvent('select_item', {
        item_list_id: listId,
        item_list_name: listName,
        items: [item],
    })
}

export function viewItem(card) {
    const item = toItem(card, { quantity: 1 })
    if (!item) {
        return
    }
    pushEvent('view_item', {
        currency: currencyCode(card),
        value: item.price,
        items: [item],
    })
}

export function addToCart(card) {
    const item = toItem(card, { quantity: 1 })
    if (!item) {
        return
    }
    pushEvent('add_to_cart', omitEmpty({
        currency: currencyCode(card),
        value: (item.price ?? 0) * (item.quantity ?? 1),
        discount: item.discount,
        items: [item],
    }))
}

export function removeFromCart(card) {
    const item = toItem(card, { quantity: 1 })
    if (!item) {
        return
    }
    pushEvent('remove_from_cart', {
        currency: currencyCode(card),
        value: (item.price ?? 0) * (item.quantity ?? 1),
        items: [item],
    })
}

export function viewCart(cards, value, currency) {
    const items = (cards || []).map((card, index) => toItem(card, { index: index + 1, quantity: card.count || 1 })).filter(Boolean)
    pushEvent('view_cart', {
        currency: currency || currencyCode(cards?.[0]),
        value: toNumber(value) ?? itemsValue(items),
        items,
    })
}

export function beginCheckout(cards, value, currency, coupon) {
    const items = (cards || []).map((card, index) => toItem(card, { index: index + 1, quantity: card.count || 1 })).filter(Boolean)
    pushEvent('begin_checkout', omitEmpty({
        currency: currency || currencyCode(cards?.[0]),
        value: toNumber(value) ?? itemsValue(items),
        coupon,
        items,
    }))
}

export function addShippingInfo(cards, value, currency, shippingTier, coupon) {
    const items = (cards || []).map((card, index) => toItem(card, { index: index + 1, quantity: card.count || 1 })).filter(Boolean)
    pushEvent('add_shipping_info', omitEmpty({
        currency: currency || currencyCode(cards?.[0]),
        value: toNumber(value) ?? itemsValue(items),
        shipping_tier: shippingTier,
        coupon,
        items,
    }))
}

export function addPaymentInfo(cards, value, currency, paymentType, coupon) {
    const items = (cards || []).map((card, index) => toItem(card, { index: index + 1, quantity: card.count || 1 })).filter(Boolean)
    pushEvent('add_payment_info', omitEmpty({
        currency: currency || currencyCode(cards?.[0]),
        value: toNumber(value) ?? itemsValue(items),
        payment_type: paymentType,
        coupon,
        items,
    }))
}

export function purchase(payload) {
    if (!payload?.transaction_id) {
        return
    }
    const key = `purchase_sent_${payload.transaction_id}`
    try {
        if (window.localStorage?.getItem(key)) {
            return
        }
        window.localStorage.setItem(key, '1')
    } catch (e) {
        // private mode / blocked storage — всё равно отправим, transaction_id дедупит в GA4
    }
    pushEvent('purchase', payload)
}

export function catalogListId(group) {
    const gender = group?.gender || store.state.settings?.gender || 'catalog'
    const link = group?.link || group?.category_link || group?.category_name || 'catalog'
    return `${gender}_${String(link).toLowerCase().replace(/\s+/g, '_')}`
}
